import { FC, useId, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { useUser } from "@framework/provider-user";
import { useSettings } from "@framework/provider-settings";
import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import { IUser } from "@framework/types";

export interface IResetUserSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  target?: Pick<IUser, "id" | "login" | "name" | "surName">;
}

export const ResetUserSettingsDialog: FC<IResetUserSettingsDialogProps> = ({ open, onClose, target }) => {
  const user = useUser<IUser>();
  const settings = useSettings();
  const asu = useAsu();
  const titleId = useId();
  const descriptionId = useId();
  const isPending = useRef(false);
  const isCurrentUser = !target || target.id === user.profile.id;
  const isResetBlocked = isCurrentUser && user.isSavingSettings;
  const { fn: resetSettings, isLoading } = useApiCall(async api => {
    await api.fetchJson({
      url: target ? `/users/${target.id}/reset-settings` : "/users/reset-settings",
      method: "POST",
    });

    if (isCurrentUser) {
      const profile = await user.getProfile();
      if (!profile) {
        return false;
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("rangeType");
      url.searchParams.delete("startTimestamp");
      url.searchParams.delete("endTimestamp");
      asu.resetTimeRange();
      settings.resetSettings(profile.settingsResetRevision);
      window.history.replaceState(window.history.state, "", url);
      window.location.reload();
    }

    return true;
  });

  const handleConfirm = async (): Promise<void> => {
    if (isPending.current || isResetBlocked) {
      return;
    }

    isPending.current = true;
    try {
      if (await resetSettings()) {
        onClose();
      }
    } finally {
      isPending.current = false;
    }
  };

  const handleClose = (): void => {
    if (!isPending.current) {
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-testid="ResetUserSettingsDialog"
    >
      <DialogTitle id={titleId}>
        <FormattedMessage id="form.buttons.resetSettings" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText id={descriptionId}>
          <FormattedMessage
            id={target ? "dialogs.resetUserSettings" : "dialogs.resetOwnSettings"}
            values={{ title: target ? `${target.name} ${target.surName} (${target.login})`.trim() : "" }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading} data-testid="DialogCancelButton">
          <FormattedMessage id="form.buttons.cancel" />
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={isLoading || isResetBlocked}
          data-testid="DialogConfirmButton"
        >
          <FormattedMessage id="form.buttons.reset" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
